import { createRouter, createWebHistory } from 'vue-router'
import { Capacitor } from '@capacitor/core'
import { useAuthStore } from '@/stores/auth'

const routes = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/Login.vue'),
    meta: { guest: true },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/Register.vue'),
    meta: { guest: true },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/ForgotPassword.vue'),
    meta: { guest: true },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/ResetPassword.vue'),
    meta: { public: true },
  },
  {
    path: '/invite/:token',
    name: 'invite-accept',
    component: () => import('@/views/InviteAccept.vue'),
    meta: { public: true },
    props: true,
  },
  {
    path: '/tickets/:token',
    name: 'public-ticket',
    component: () => import('@/views/PublicTicket.vue'),
    meta: { public: true },
    props: true,
  },
  {
    // Portal público de solicitudes con sidebar (equipos → tableros → formulario).
    // El token preselecciona un equipo (enlace directo desde Ajustes).
    path: '/equipos/:token',
    name: 'public-portal',
    component: () => import('@/views/PublicPortal.vue'),
    meta: { public: true },
  },
  {
    // Sin token no hay portal que mostrar: /equipos manda a la vista interna
    // de Equipos (evita confusión con el magic link público).
    path: '/equipos',
    redirect: { name: 'team-equipos' },
  },
  // Compat: el enlace directo por equipo ahora entra al portal preseleccionado.
  {
    path: '/equipo/:token',
    redirect: (to) => ({ name: 'public-portal', params: { token: to.params.token } }),
  },
  {
    path: '/force-upgrade',
    name: 'force-upgrade',
    component: () => import('@/views/ForceUpgrade.vue'),
    meta: { public: true },
  },
  {
    path: '/onboarding/permissions',
    name: 'permissions-onboarding',
    component: () => import('@/views/onboarding/PermissionsOnboarding.vue'),
    meta: { mobileOnly: true },
  },
  {
    path: '/onboarding/oem-whitelist',
    name: 'oem-whitelist-onboarding',
    component: () => import('@/views/onboarding/OemWhitelistOnboarding.vue'),
    meta: { mobileOnly: true },
  },
  {
    path: '/welcome',
    name: 'welcome',
    component: () => import('@/views/onboarding/Welcome.vue'),
    meta: { guest: true, mobileOnly: true },
  },
  {
    path: '/tour',
    name: 'tour',
    component: () => import('@/views/onboarding/Tour.vue'),
    meta: { mobileOnly: true },
  },
  {
    path: '/recovery',
    name: 'recovery-recordings',
    component: () => import('@/views/RecoveryRecordings.vue'),
    meta: { mobileOnly: true },
  },
  {
    path: '/docs/api',
    name: 'api-docs',
    component: () => import('@/views/ApiDocs.vue'),
    meta: { public: true },
  },
  {
    path: '/teams/config',
    name: 'teams-config',
    component: () => import('@/views/TeamsTabConfig.vue'),
    meta: { public: true },
  },
  {
    path: '/teams/auth-start',
    name: 'teams-auth-start',
    component: () => import('@/views/TeamsAuthStart.vue'),
    meta: { public: true },
  },
  {
    path: '/teams/auth-end',
    name: 'teams-auth-end',
    component: () => import('@/views/TeamsAuthEnd.vue'),
    meta: { public: true },
  },
  {
    path: '/',
    component: () => import('@/layouts/MarketingLayout.vue'),
    meta: { public: true },
    beforeEnter: () => {
      // On native the marketing site is never shown. Route straight to the
      // app or to login depending on the (already-hydrated) session — do NOT
      // hardcode /login, or an authenticated user lands on login and stays
      // stranded there until they happen to trigger another navigation.
      if (Capacitor.isNativePlatform()) {
        const auth = useAuthStore()
        return auth.isAuthenticated ? { name: 'dashboard' } : { name: 'login' }
      }
    },
    children: [
      {
        path: '',
        name: 'landing',
        component: () => import('@/views/marketing/Landing.vue'),
        meta: { public: true },
      },
      {
        path: 'privacy',
        name: 'privacy',
        component: () => import('@/views/marketing/Privacy.vue'),
        meta: { public: true },
      },
      {
        path: 'terms',
        name: 'terms',
        component: () => import('@/views/marketing/Terms.vue'),
        meta: { public: true },
      },
      {
        path: 'changelog',
        name: 'changelog',
        component: () => import('@/views/marketing/Changelog.vue'),
        meta: { public: true },
      },
      {
        path: 'security',
        name: 'security',
        component: () => import('@/views/marketing/Security.vue'),
        meta: { public: true },
      },
    ],
  },
  // La landing enlaza a precios como anchor (/#pricing), pero la URL directa
  // /pricing circula en emails y auditorías — mantenerla viva como redirect.
  {
    path: '/pricing',
    redirect: { path: '/', hash: '#pricing' },
  },
  // URL legacy de cuando seguridad era un HTML estático en public/legal/.
  {
    path: '/legal/security.html',
    redirect: { name: 'security' },
  },
  {
    path: '/',
    component: () => import('@/layouts/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/views/Home.vue'),
      },
      {
        path: 'projects',
        name: 'projects',
        component: () => import('@/views/Dashboard.vue'),
      },
      {
        path: 'my-tasks',
        name: 'my-tasks',
        component: () => import('@/views/MyTasks.vue'),
      },
      {
        path: 'projects/:id',
        name: 'project',
        component: () => import('@/views/Project.vue'),
        props: true,
      },
      {
        // #4001 — enlace canónico para compartir una tarea. Resuelve a qué
        // tablero pertenece y redirige; las tareas sin proyecto se abren aquí.
        path: 'tasks/:id',
        name: 'task',
        component: () => import('@/views/TaskLink.vue'),
        props: true,
      },
      {
        path: 'billing',
        name: 'billing',
        component: () => import('@/views/Billing.vue'),
      },
      {
        path: 'notifications',
        name: 'notifications',
        component: () => import('@/views/Notifications.vue'),
      },
      {
        path: 'settings/notifications',
        name: 'settings-notifications',
        component: () => import('@/views/Settings.vue'),
      },
      {
        path: 'team/members',
        name: 'team-members',
        component: () => import('@/views/TeamMembers.vue'),
      },
      {
        path: 'teams',
        name: 'team-equipos',
        component: () => import('@/views/Equipos.vue'),
      },
      {
        path: 'teams/:id',
        name: 'equipo-detail',
        component: () => import('@/views/EquipoDetail.vue'),
        props: true,
      },
      {
        path: 'team/members/:userId',
        name: 'team-member-profile',
        component: () => import('@/views/MemberProfile.vue'),
        props: true,
      },
      {
        // #4533: todas las tareas asignadas a un miembro, desde su perfil.
        path: 'team/members/:userId/tasks',
        name: 'team-member-tasks',
        component: () => import('@/views/MemberTasks.vue'),
        props: true,
      },
      {
        path: 'reports',
        name: 'reports',
        component: () => import('@/views/Reports.vue'),
      },
      {
        path: 'portfolio',
        name: 'portfolio',
        component: () => import('@/views/Portfolio.vue'),
      },
      {
        path: 'activity',
        name: 'activity',
        component: () => import('@/views/Activity.vue'),
      },
      {
        path: 'meetings',
        name: 'meetings',
        component: () => import('@/views/Meetings.vue'),
      },
      {
        path: 'meetings/:id',
        name: 'meeting-review',
        component: () => import('@/views/MeetingReview.vue'),
        props: true,
        // Flujo enfocado con su propia barra de acciones `fixed bottom-0`:
        // el bottom nav móvil taparía el botón de confirmar.
        meta: { hideBottomNav: true },
      },
      {
        path: 'transcripts',
        name: 'transcripts',
        component: () => import('@/views/Transcripts.vue'),
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFound.vue'),
    meta: { public: true },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.hash) return { el: to.hash, behavior: 'smooth' }
  },
})

router.beforeEach(async (to) => {
  const auth = useAuthStore()

  if (auth.token && !auth.user) {
    await auth.fetchMe()
  }

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return { name: 'login' }
  }

  if (to.meta.guest && auth.isAuthenticated) {
    return { name: 'dashboard' }
  }
})

export default router
